<script setup>

import StaticWidget from "@/components/common/other/StaticWidget";
import SearchToolbar from "@/components/shipments/SearchToolbar";
import Button from "@/components/common/input/Button";
import StaticTooltip from "@/components/common/other/StaticTooltip"
import ViewShipmentFromTo from "@/components/shipments/ViewShipmentFromTo"
import ConsignmentAndMilestones from "@/components/shipments/ConsignmentAndMilestones";
import ViewShipmentTabsArea from "@/components/shipments/ViewShipmentTabsArea";
import SlideInDrawer from "@/components/common/navigation/SlideInDrawer";
import ShipmentDocumentDownload from "@/components/shipments/ShipmentDocumentDownload";
import LoadingSpinner from "@/components/common/other/LoadingSpinner";
import {ViewShipment} from "@/classes/viewmodels/ViewShipment";
import API from "@/classes/global/API";

import {useGetDocumentsList} from "@/composables/common/useGetDocumentsList";
import {useCreateSubstringText} from "@/composables/common/useCreateSubstringText";
import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useRouter} from 'vue-router';

import {ref, inject, onMounted} from 'vue';

const shipments = inject("shipments");
const store = useStore();
const route = useRoute();
const router = useRouter();

let jobNumberCopied = ref(false);
let custRefCopied = ref(false);

function saveToClipboard(text, heading) {

  navigator.clipboard.writeText(text);

  if (heading === 'jobNumber') jobNumberCopied.value = true;
  if (heading === 'custRef') custRefCopied.value = true;

  setTimeout(function () {

    jobNumberCopied.value = false;
    custRefCopied.value = false;

  }, 2000);

}

async function getSelectedShipment() {

  const shipment = await API.getShipmentData(route);

  if (shipment) {

    store.state.hideLoadingSpinner = true;

    shipments.value.viewShipment = shipment;

  } else {

    shipments.value.viewShipment = new ViewShipment();
  }

}

function checkModeForBol() {

  if (shipments.value.mode === shipments.value.modes.road) return true;
  if (shipments.value.mode === shipments.value.modes.sea) return true;

  return false;

}

function editShipment(routeParams) {

  let bookingType = routeParams.mode;

  // Have to convert the Other & Courier sent from the selected tab, to the respective names for the services.

  if (routeParams.mode === 'Other') bookingType = 'freightBooking';
  if (routeParams.mode === 'Courier') bookingType = 'courierBooking';

  if (bookingType === 'freightBooking' || bookingType === 'courierBooking') {

    router.push({name: 'booking', params: {owner: store.state.owner, section: bookingType, id: route.params.id}})

  } else {

    router.push({name: 'prejob', params: {owner: store.state.owner, section: routeParams.mode, id: route.params.id}})
  }

}

onMounted(async () => {

  await router.isReady();

  getSelectedShipment();

})

</script>

<template id="Vue-3-view-component-view-shipments" class="view-view-shipments">
  <LoadingSpinner :hide="store.state.hideLoadingSpinner"></LoadingSpinner>
  <SlideInDrawer
      title="Available Documents"
      v-model:open-drawer="shipments.shipmentDocsDrawer">
    <template #icon><i class="fa-solid fa-folder-open" style="color: #eab410;"></i></template>
    <ShipmentDocumentDownload></ShipmentDocumentDownload>
  </SlideInDrawer>
  <StaticWidget
      height="735"
      title="Shipment Details">
    <template #right-side>
      <Button
          v-if="shipments.viewShipment.isEditable"
          @click="editShipment(route.params)"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Edit"
          type="info">
      </Button>
      <Button
          @click="useGetDocumentsList(route.params.id, route, store, shipments)"
          type="warning"
          icon="<i class='fa-duotone fa-folder-open'></i>"
          text="Docs">
      </Button>
      <Button
          :disabled="true"
          type="secondary"
          icon="<i class='fa-duotone fa-tag'></i>"
          title="Later Feature"
          text="Label">
      </Button>
      <Button
          v-if="checkModeForBol()"
          text="BOL">
      </Button>
    </template>
    <SearchToolbar>
      <div class="toolbar-content">
        <div style="width: 340px; margin-left: 50px;">
          <StaticTooltip v-if="!jobNumberCopied">
            <template #content>Copy To Clipboard</template>
            <i class="fa-solid fa-clipboard view-view-shipments_toolbar-clipboard-icons"
               @click="saveToClipboard(shipments.viewShipment.jobNumber, 'jobNumber')">
            </i>
          </StaticTooltip>
          <i v-else class="fa-solid fa-circle-check view-view-shipments_copied-success"></i>
          <span class="view-view-shipments_toolbar-headings">Job Number:</span>
          <span class="view-view-shipments_toolbar-data">{{ shipments.viewShipment.jobNumber }}</span>
        </div>
        <div style="width: 740px;">
          <StaticTooltip v-if="!custRefCopied">
            <template #content>Copy To Clipboard</template>
            <i class="fa-solid fa-clipboard view-view-shipments_toolbar-clipboard-icons"
               @click="saveToClipboard(shipments.viewShipment.customerRef, 'custRef')">
            </i>
          </StaticTooltip>
          <i v-else class="fa-solid fa-circle-check view-view-shipments_copied-success"></i>
          <StaticTooltip :icon="false">
            <template #content>
              {{ shipments.viewShipment.customerRef || 'No Data' }}
            </template>
            <span class="view-view-shipments_toolbar-headings">Customer Ref:</span>
            <span :class="{'view-view-shipments_toolbar-data' : shipments.viewShipment.customerRef,
             'global-no-data-placeholder' : !shipments.viewShipment.customerRef }">
              {{ useCreateSubstringText(shipments.viewShipment.customerRef, 80) || ' Customer Ref......' }}
            </span>
          </StaticTooltip>
        </div>
        <div style="width: 300px;">
          <i class="fa-solid fa-truck-plane" style="color: #46a9b3;"></i>
          <span class="view-view-shipments_toolbar-headings">Mode:</span><span
            class="view-view-shipments_toolbar-data">{{ shipments.viewShipment.mode }}</span>
        </div>
      </div>
    </SearchToolbar>

    <ViewShipmentFromTo></ViewShipmentFromTo>

    <ConsignmentAndMilestones></ConsignmentAndMilestones>

    <ViewShipmentTabsArea></ViewShipmentTabsArea>

    <template v-slot:icon>
      <i class="fa-solid fa-square-list" style="color: #bcb46a;"></i>
    </template>
  </StaticWidget>
</template>

<style lang="scss" scoped>

.view-view-shipments {

  &_toolbar-headings {
    font-weight: bold;
    margin-left: 5px;
  }

  &_toolbar-clipboard-icons {
    color: #3780d0;
    cursor: pointer;
    width: 13px;
  }

  &_toolbar-data {
    margin-left: 5px;
  }

  &_copied-success {
    color: #3f9968;
    width: 13px;
  }

}


</style>
